import React, { useEffect, useState } from 'react';
import { investmentPlanApi, InvestmentPlanInput } from '../services/investmentPlanApi.ts';
import InvestmentProjection from '../components/InvestmentProjection.tsx';
import InvestmentProjectionChart from '../components/InvestmentProjectionChart.tsx';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import { parseNumberInput, parsePercentInput } from '../utils/numberUtils.ts';
import './InvestmentPlan.css';

interface InvestmentPlan {
  id: number;
  name: string;
  initial_net_worth: number;
  monthly_contribution: number;
  withdrawal_amount: number;
  expected_nominal_annual_return: number;
  expected_annual_inflation: number;
  current_age: number;
  withdrawal_start_age: number;
  life_expectancy: number;
}

interface InvestmentPlanFormState {
  name: string;
  current_age: string;
  withdrawal_start_age: string;
  life_expectancy: string;
  initial_net_worth: string;
  monthly_contribution: string;
  withdrawal_amount: string;
  expected_nominal_annual_return: string;
  expected_annual_inflation: string;
}

const InvestmentPlanPage: React.FC = () => {
  const [plans, setPlans] = useState<InvestmentPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [projection, setProjection] = useState<any>(null);
  const [strategyProjections, setStrategyProjections] = useState<Record<string, any> | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [isProjectionModalOpen, setIsProjectionModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;
  const [form, setForm] = useState<InvestmentPlanFormState>({
    name: '',
    current_age: '30',
    withdrawal_start_age: '60',
    life_expectancy: '90',
    initial_net_worth: '',
    monthly_contribution: '',
    withdrawal_amount: '',
    expected_nominal_annual_return: '8',
    expected_annual_inflation: '4',
  });
  const [saving, setSaving] = useState(false);
  const [projectingId, setProjectingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (backendAvailable) {
      loadPlans();
    }
  }, [backendAvailable]);

  const loadPlans = async () => {
    const response = await investmentPlanApi.listPlans();
    setPlans(response.data || []);
  };

  const handleSubmitPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: InvestmentPlanInput = {
        name: form.name,
        current_age: parseNumberInput(form.current_age),
        withdrawal_start_age: parseNumberInput(form.withdrawal_start_age),
        life_expectancy: parseNumberInput(form.life_expectancy),
        initial_net_worth: parseNumberInput(form.initial_net_worth),
        monthly_contribution: parseNumberInput(form.monthly_contribution),
        withdrawal_amount: parseNumberInput(form.withdrawal_amount),
        expected_nominal_annual_return: parsePercentInput(form.expected_nominal_annual_return),
        expected_annual_inflation: parsePercentInput(form.expected_annual_inflation),
      };
      if (editingPlanId) {
        const response = await investmentPlanApi.updatePlan(editingPlanId, payload);
        const updatedPlan = response.data;
        setPlans((prev) => prev.map((plan) => (plan.id === updatedPlan.id ? updatedPlan : plan)));
        setSelectedPlanId(updatedPlan.id);
        setEditingPlanId(null);
      } else {
        const response = await investmentPlanApi.createPlan(payload);
        const newPlan = response.data;
        setPlans((prev) => [...prev, newPlan]);
        setSelectedPlanId(newPlan.id);
      }
      setProjection(null);
      setStrategyProjections(null);
      setIsFormModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleProjection = async (planId: number) => {
    if (!planId) return;
    if (!backendAvailable) return;
    setProjectingId(planId);
    try {
      const strategies = ['sustainable', 'partial', 'full'];
      const responses = await Promise.all(
      strategies.map((strategy) => investmentPlanApi.generateProjection(planId, undefined, strategy))
    );
    const projectionsByStrategy = responses.reduce<Record<string, any>>((acc, response, index) => {
      acc[strategies[index]] = response.data.projection;
      return acc;
    }, {});
      setProjection(projectionsByStrategy.partial);
      setStrategyProjections(projectionsByStrategy);
      setSelectedPlanId(planId);
      setIsProjectionModalOpen(true);
    } finally {
      setProjectingId(null);
    }
  };

  const handleDeletePlan = async (plan: InvestmentPlan) => {
    const confirmed = window.confirm(`Excluir o plano "${plan.name}"? Esta ação não pode ser desfeita.`);
    if (!confirmed) return;
    setDeletingId(plan.id);
    try {
      await investmentPlanApi.deletePlan(plan.id);
      setPlans((prev) => prev.filter((item) => item.id !== plan.id));
      if (selectedPlanId === plan.id) {
        setSelectedPlanId(null);
        setProjection(null);
        setStrategyProjections(null);
        setIsProjectionModalOpen(false);
      }
      if (editingPlanId === plan.id) {
        setEditingPlanId(null);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const chartPoints =
    projection?.points?.map((point: any) => ({
      age: point.age,
      nominal: point.nominal_net_worth,
      real: point.real_net_worth,
    })) ?? [];
  const strategyChartPoints = strategyProjections
    ? Object.entries(strategyProjections).reduce<Record<string, any[]>>((acc, [strategy, projectionData]) => {
        acc[strategy] =
          projectionData?.points?.map((point: any) => ({
            age: point.age,
            nominal: point.nominal_net_worth,
            real: point.real_net_worth,
          })) ?? [];
        return acc;
      }, {})
    : null;
  const strategyWithdrawals = strategyProjections
    ? Object.entries(strategyProjections).reduce<Record<string, number>>((acc, [strategy, projectionData]) => {
        const points = projectionData?.points ?? [];
        const firstWithdrawal = points.find((point: any) => point.withdrawal > 0 || point.phase === 'withdrawal');
        acc[strategy] = firstWithdrawal?.withdrawal ?? 0;
        return acc;
      }, {})
    : null;

  const formatAmountForDisplay = (value: number): string => {
    if (value === 0) return '';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  const formatPercentForDisplay = (value: number): string => {
    const percent = value * 100;
    return `${percent.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}%`;
  };
  const formatPercentForInput = (value: number): string => {
    return (value * 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseAmountFromInput = (input: string): number => {
    if (!input) return 0;
    const cleanInput = input.replace(/[^\d,]/g, '');
    const numericString = cleanInput.replace(',', '.');
    return parseFloat(numericString) || 0;
  };

  const handleMoneyChange = (field: 'monthly_contribution' | 'initial_net_worth' | 'withdrawal_amount', value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMoneyBlur = (field: 'monthly_contribution' | 'initial_net_worth' | 'withdrawal_amount') => {
    const numericValue = parseAmountFromInput(form[field]);
    setForm((prev) => ({
      ...prev,
      [field]: numericValue > 0 ? formatAmountForDisplay(numericValue) : ''
    }));
  };

  const handleEditPlan = (plan: InvestmentPlan) => {
    setForm({
      name: plan.name,
      current_age: String(plan.current_age),
      withdrawal_start_age: String(plan.withdrawal_start_age),
      life_expectancy: String(plan.life_expectancy),
      initial_net_worth: formatAmountForDisplay(plan.initial_net_worth),
      monthly_contribution: formatAmountForDisplay(plan.monthly_contribution),
      withdrawal_amount: formatAmountForDisplay(plan.withdrawal_amount),
      expected_nominal_annual_return: formatPercentForInput(plan.expected_nominal_annual_return),
      expected_annual_inflation: formatPercentForInput(plan.expected_annual_inflation),
    });
    setEditingPlanId(plan.id);
    setIsFormModalOpen(true);
  };

  const handleOpenNewPlan = () => {
    setForm({
      name: '',
      current_age: '30',
      withdrawal_start_age: '60',
      life_expectancy: '90',
      initial_net_worth: '',
      monthly_contribution: '',
      withdrawal_amount: '',
      expected_nominal_annual_return: '8',
      expected_annual_inflation: '4',
    });
    setEditingPlanId(null);
    setIsFormModalOpen(true);
  };

  return (
    <div className="investment-plan module-name">
      <div className="page-header">
        <div>
          <h1>Plano de Investimentos</h1>
          <p>Configure seu plano e gere projeções de longo prazo.</p>
        </div>
      </div>

      {!backendAvailable && (
        <div className="message error" style={{ marginBottom: '16px' }}>
          ⚠️ Projeções exigem conexão com o backend. Cadastro e edição continuam disponíveis offline.
        </div>
      )}

      <div className="module-content">
        <div className="plan-list">
          <h3>Planos salvos</h3>
          <button className="primary-button" type="button" onClick={handleOpenNewPlan}>
            Novo plano
          </button>
          <div className="plan-table">
            <div className="plan-table-header">
              <span>Nome do plano</span>
              <span>Retorno anual</span>
              <span>Inflação anual</span>
              <span>Ações</span>
            </div>
            {plans.length === 0 && (
              <div className="plan-table-row empty">
                <span>Nenhum plano salvo.</span>
              </div>
            )}
            {plans.map((plan) => (
              <div className="plan-table-row" key={plan.id}>
                <span data-label="Nome do plano">{plan.name}</span>
                <span data-label="Retorno anual">{formatPercentForDisplay(plan.expected_nominal_annual_return)}</span>
                <span data-label="Inflação anual">{formatPercentForDisplay(plan.expected_annual_inflation)}</span>
                <div className="plan-table-actions">
                  <button
                    className="action-icon-button"
                    type="button"
                    onClick={() => handleProjection(plan.id)}
                    title="Gerar plano"
                    aria-label="Gerar plano"
                    disabled={!backendAvailable || projectingId === plan.id}
                  >
                    {projectingId === plan.id ? '...' : <span className="icon-emoji" aria-hidden="true">📈</span>}
                  </button>
                  <button
                    className="action-icon-button"
                    type="button"
                    onClick={() => handleEditPlan(plan)}
                    title="Editar plano"
                    aria-label="Editar plano"
                  >
                    <img src="/edit.svg" alt="" className="icon" />
                  </button>
                  <button
                    className="action-icon-button delete"
                    type="button"
                    onClick={() => handleDeletePlan(plan)}
                    title="Excluir plano"
                    aria-label="Excluir plano"
                    disabled={deletingId === plan.id}
                  >
                    {deletingId === plan.id ? '...' : <img src="/delete.svg" alt="" className="icon" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {isProjectionModalOpen && (
            <div className="projection-modal-overlay">
              <div className="projection-modal">
                <button
                  className="projection-modal-close"
                  type="button"
                  onClick={() => setIsProjectionModalOpen(false)}
                  aria-label="Fechar projeção"
                >
                  ✕
                </button>
                <InvestmentProjectionChart
                  points={chartPoints}
                  withdrawalStartAge={selectedPlan?.withdrawal_start_age}
                  strategyPoints={strategyChartPoints}
                  strategyWithdrawals={strategyWithdrawals}
                />
                <InvestmentProjection projection={projection} alert={projection?.depletion_alert} />
              </div>
            </div>
          )}

          {isFormModalOpen && (
            <div className="plan-form-modal-overlay">
              <div className="plan-form-modal">
                <button
                  className="plan-form-modal-close"
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  aria-label="Fechar formulário"
                >
                  ✕
                </button>
                <form className="form-container investment-plan-form" onSubmit={handleSubmitPlan}>
                  <h3>{editingPlanId ? 'Editar plano' : 'Novo plano'}</h3>
                  <div className="form-group">
                    <label>Nome do plano</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Idade atual</label>
                      <input
                        type="text"
                        value={form.current_age}
                        onChange={(e) => setForm({ ...form, current_age: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Início da retirada</label>
                      <input
                        type="text"
                        value={form.withdrawal_start_age}
                        onChange={(e) => setForm({ ...form, withdrawal_start_age: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expectativa de vida</label>
                      <input
                        type="text"
                        value={form.life_expectancy}
                        onChange={(e) => setForm({ ...form, life_expectancy: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contribuição mensal</label>
                      <input
                        type="text"
                        value={form.monthly_contribution}
                        onChange={(e) => handleMoneyChange('monthly_contribution', e.target.value)}
                        onBlur={() => handleMoneyBlur('monthly_contribution')}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Patrimônio inicial</label>
                      <input
                        type="text"
                        value={form.initial_net_worth}
                        onChange={(e) => handleMoneyChange('initial_net_worth', e.target.value)}
                        onBlur={() => handleMoneyBlur('initial_net_worth')}
                      />
                    </div>
                    <div className="form-group">
                      <label>Retirada mensal planejada</label>
                      <input
                        type="text"
                        value={form.withdrawal_amount}
                        onChange={(e) => handleMoneyChange('withdrawal_amount', e.target.value)}
                        onBlur={() => handleMoneyBlur('withdrawal_amount')}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Retorno nominal anual (%)</label>
                      <input
                        type="text"
                        value={form.expected_nominal_annual_return}
                        onChange={(e) => setForm({ ...form, expected_nominal_annual_return: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Inflação anual (%)</label>
                      <input
                        type="text"
                        value={form.expected_annual_inflation}
                        onChange={(e) => setForm({ ...form, expected_annual_inflation: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button className="primary-button" type="submit" disabled={saving}>
                      {saving ? 'Salvando...' : (editingPlanId ? 'Salvar alterações' : 'Salvar plano')}
                    </button>
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => setIsFormModalOpen(false)}
                      disabled={saving}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentPlanPage;
